import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import CenteredSpinner from "./CenteredSpinner.tsx";
import EmptyState from "./EmptyState.tsx";

export interface DistributionDatum {
  label: string;
  count: number;
  failedCount?: number;
}

interface DistributionChartProps {
  title: string;
  data: DistributionDatum[];
  loading?: boolean;
  showPercent?: boolean;
  sort?: "count" | "percent";
}

export default function DistributionChart({
  title,
  data,
  loading = false,
  showPercent = false,
  sort = "count",
}: DistributionChartProps) {
  const sorted = [...data].sort((a, b) => {
    if (sort === "percent") {
      const aPct = a.count > 0 ? (a.count - (a.failedCount ?? 0)) / a.count : 0;
      const bPct = b.count > 0 ? (b.count - (b.failedCount ?? 0)) / b.count : 0;
      return bPct - aPct;
    }
    return b.count - a.count;
  });
  const max = [...data].sort((a, b) => b.count - a.count)[0]?.count ?? 1;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1.5 }}>
        {title}
      </Typography>
      {loading ? (
        <CenteredSpinner size={24} />
      ) : sorted.length === 0 ? (
        <EmptyState title="No data yet" />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {sorted.map(({ label, count, failedCount }) => {
            const okCount = count - (failedCount ?? 0);
            const hasBuffer = failedCount !== undefined;
            return (
              <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    minWidth: 120,
                    flexShrink: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={label}
                >
                  {label}
                </Typography>
                <LinearProgress
                  variant={hasBuffer ? "buffer" : "determinate"}
                  value={(okCount / max) * 100}
                  valueBuffer={hasBuffer ? (count / max) * 100 : undefined}
                  sx={{ flex: 1, height: 8, borderRadius: 1 }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    minWidth: 40,
                    textAlign: "right",
                    color: "text.secondary",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {hasBuffer ? `${okCount}/${count}` : count}
                </Typography>
                {showPercent && hasBuffer && (
                  <Typography
                    variant="body2"
                    sx={{
                      minWidth: 44,
                      textAlign: "right",
                      color: "text.primary",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {count > 0 ? Math.round((okCount / count) * 100) : 0}%
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
}
